import React, { PropsWithChildren, useContext } from 'react';

import type { ChannelState as StreamChannelState } from 'belo-js-sdk';

import type { DefaultBeloChatGenerics, UnknownType } from '../types/types';

export type TypingContextValue<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  typing?: StreamChannelState<BeloChatGenerics>['typing'];
};

export const TypingContext = React.createContext<TypingContextValue | undefined>(undefined);

export const TypingProvider = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>({
  children,
  value,
}: PropsWithChildren<{
  value: TypingContextValue<BeloChatGenerics>;
}>) => (
  <TypingContext.Provider value={(value as unknown) as TypingContextValue}>
    {children}
  </TypingContext.Provider>
);

export const useTypingContext = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  componentName?: string,
) => {
  const contextValue = useContext(TypingContext);

  if (!contextValue) {
    console.warn(
      `The useTypingContext hook was called outside of the TypingContext provider. Make sure this hook is called within a child of the Channel component. The errored call is located in the ${componentName} component.`,
    );

    return {} as TypingContextValue<BeloChatGenerics>;
  }

  return contextValue as TypingContextValue<BeloChatGenerics>;
};

/**
 * Typescript currently does not support partial inference, so if TypingContext
 * typing is desired while using the HOC withTypingContext, the Props for the
 * wrapped component must be provided as the first generic.
 */
export const withTypingContext = <
  P extends UnknownType,
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  Component: React.ComponentType<P>,
) => {
  const WithTypingContextComponent = (
    props: Omit<P, keyof TypingContextValue<BeloChatGenerics>>,
  ) => {
    const typingContext = useTypingContext<BeloChatGenerics>();

    return <Component {...(props as P)} {...typingContext} />;
  };

  WithTypingContextComponent.displayName = (
    Component.displayName ||
    Component.name ||
    'Component'
  ).replace('Base', '');

  return WithTypingContextComponent;
};
